import * as React from "react";
import {
  TextField,
  BooleanField,
  ImageField,
  Show,
  NumberField,
  DateField,
  FunctionField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  usePermissions,
  useRecordContext,
  Title,
  RichTextField,
  ArrayField,
  SelectField,
  useNotify,
  useDataProvider,
  useRefresh,
  LinearProgress,
  TopToolbar,
  EditButton,
  Button,
  DataTable,
} from "react-admin";
import { ROLE_TYPES } from "../enums/Roles";
import { SimpleShowLayout } from "../common/SimpleShowLayout";
import { AuditButton } from "../common/AuditButton";
import { Archive } from "@mui/icons-material";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "../utils/EntityAvailabilityStatusUtils";
import { shortWeekdayToLong } from "../utils/DateTimeUtils";
import {
  BANNER_SCHEDULE_TYPES,
  BANNER_SCHEDULE_TYPES_CHOICES,
} from "../enums/BannerScheduleTypes";
import { ALL_DAYS } from "../utils/ProductUtils";
import { BANNER_TYPES } from "../enums/BannerTypes";

const BannerShowActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = React.useState(false);
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const handleArchiveSection = async () => {
    notify("Archivando baner...");
    setIsLoading(true);
    dataProvider
      .update("banners", {
        data: {
          ...record,
          status: ENTITY_AVAILABILITY_STATUS.ARCHIVED,
        },
      })
      .then(() => {
        notify("Baner archivado con éxito", {
          type: "success",
        });
        refresh();
      })
      .catch(() => {
        notify("Ha ocurrido un error archivando el baner", {
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) return <LinearProgress />;

  return (
    <TopToolbar>
      <EditButton />
      {record?.status !== ENTITY_AVAILABILITY_STATUS.ARCHIVED && (
        <Button onClick={handleArchiveSection} label="Archivar">
          <Archive />
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && <AuditButton />}
    </TopToolbar>
  );
};

const BannerTitle = () => {
  const record = useRecordContext();
  return <Title title={`Banner "${record?.title}"`} />;
};

const BannerShow = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  return (
    <SimpleShowLayout>
      {permissions === ROLE_TYPES.ADMIN && <TextField source="id" label="ID" />}
      <TextField source="title" label="Título" />
      <ImageField source="image" label="Imagen" />
      <TextField source="type" label="Tipo" />
      {record?.type === BANNER_TYPES.PRODUCT && (
        <ReferenceArrayField
          label="Producto(s)"
          reference="products"
          source="products"
        >
          <SingleFieldList linkType="show">
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
      )}
      {record?.type === BANNER_TYPES.RESTAURANT && [
        <ReferenceArrayField
          key="restaurants"
          label="Restaurante(s)"
          reference="restaurants"
          source="restaurants"
        >
          <SingleFieldList linkType="show">
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>,
        <RichTextField
          key="description"
          source="description"
          label="Descripción"
        />,
        <ImageField
          key="eventImage"
          source="eventImage"
          label="Imagen de Evento"
        />,
      ]}
      {record?.type === BANNER_TYPES.VIDEO && (
        <ReferenceArrayField
          label="Video(s)"
          reference="videos"
          source="videos"
          link={false}
        >
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
      )}
      <SelectField
        source="scheduleType"
        label="Tipo de Recurrencia"
        choices={BANNER_SCHEDULE_TYPES_CHOICES}
      />
      {record?.scheduleType === BANNER_SCHEDULE_TYPES.ONE_TIME && [
        <DateField
          key="startDate"
          source="startDate"
          label="Fecha Inicio"
          showTime={true}
        />,
        <DateField
          key="endDate"
          source="endDate"
          label="Fecha Fin"
          showTime={true}
        />,
        <FunctionField
          key="oneTimeAvailableDays"
          source="oneTimeAvailableDays"
          render={(record) =>
            `${
              record.oneTimeAvailableDays
                ?.slice()
                .sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b))
                .map((d) => shortWeekdayToLong(d))
                .join(", ") || "-"
            }`
          }
          label="Días disponibles"
        />,
      ]}
      {record?.scheduleType === BANNER_SCHEDULE_TYPES.RECURRENT && [
        <ArrayField
          key="recurrentSchedule"
          source="recurrentSchedule"
          label="Días y Horarios"
        >
          <DataTable rowClick={false} bulkActionButtons={false}>
            <DataTable.Col label="Días">
              <FunctionField
                label="Días"
                render={(record) =>
                  record?.days?.map(shortWeekdayToLong).join(", ") || "-"
                }
              />
            </DataTable.Col>
            <DataTable.Col source="times" label="Horarios">
              <ArrayField label="Horarios" source="times">
                <SingleFieldList linkType={false}>
                  <FunctionField
                    render={(record) => (
                      <ChipField
                        record={{
                          formattedHours: record.from + " - " + record.to,
                        }}
                        source="formattedHours"
                        size="small"
                      />
                    )}
                  />
                </SingleFieldList>
              </ArrayField>
            </DataTable.Col>
          </DataTable>
        </ArrayField>,
      ]}
      <ArrayField label="Países" source="countries">
        <SingleFieldList linkType={false}>
          <FunctionField
            render={(record) => (
              <ChipField record={{ name: record }} source="name" size="small" />
            )}
          />
        </SingleFieldList>
      </ArrayField>
      <NumberField source="order" label="Orden" />
      <BooleanField source="visible" label="Visible" />
      <BooleanField source="featured" label="Destacado" />
      <SelectField
        label="Estado"
        source="status"
        choices={ENTITY_AVAILABILITY_STATUS_OPTIONS}
      />
    </SimpleShowLayout>
  );
};

const BannerShowContainer = () => (
  <Show title={<BannerTitle />} actions={<BannerShowActions />}>
    <BannerShow />
  </Show>
);

export default BannerShowContainer;
